import {OfferAccessFlow} from 'modules/sapiSearch/constants'
import {DEFAULT_OFFER_TITLE_KEY} from 'utils/roomOfferTitle/offerTitleKeyMapping'

import {getHasFreeCancellation} from '@vio/core/src/offer/business/cancellationPenalties'
import {calculateChargeDate} from '@vio/core/src/offer/business/chargeLater'
import {getLinkByType} from '@vio/core/src/offer/business/getLinkByType'
import {BOFH_PROXY_PROVIDER_CODE} from '@vio/core/src/offer/business/isBofhOffer'
import {
  hasChargeLaterPayment,
  hasPayAtPropertyPayment
} from '@vio/core/src/offer/business/offerPaymentMode'
import {getChargeTypes} from '@vio/core/src/offer/business/offers'
import {ActionLinkTypes} from '@vio/core/src/offer/types/actionLink'
import {type Offer} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalAtProperty,
  getTotalPrice,
  getTotalTaxesAndFeesPrice,
  getTotalUpFrontPrice
} from '@vio/core/src/price/business/price'
import {getNightlyDisplayTotalPrice} from '@vio/core/src/room/business/transformSapiRoomsResponse/utils/price'
import {getTopRoomAmenities} from '@vio/shared/src/accommodation/RoomAmenities/utils'

import type {
  RoomContext,
  RoomOfferContext
} from '@vio/core/src/analytics/types/Events'
import type {Room, RoomWithoutOffers} from '@vio/core/src/room/types/room'

const ANALYTICS_OFFER_TITLE_SOLD_OUT = 'soldOut' as const
const ANALYTICS_OFFER_TITLE_CHEAPEST = 'isCheapestOffer' as const

export const buildRoomOfferContext = (
  offer: Offer,
  offerIndex: number,
  room: Room | RoomWithoutOffers,
  roomPosition: number,
  isPrivateDeal: boolean,
  isAppExclusiveRate: boolean,
  flowType: OfferAccessFlow,
  anchorPrice: number,
  roomSearchId?: string,
  isOfferSoldOut = false,
  isCheapestOffer = false
): RoomOfferContext => {
  const isChargeLaterEligible = hasChargeLaterPayment(offer)
  const chargeDate = calculateChargeDate(offer.cancellationPenalties)
  const chargeType = getChargeTypes({
    isExistingBooking: false,
    isChargeLaterEligible,
    isPayAtProperty: hasPayAtPropertyPayment(offer)
  })

  const paymentExpiryAt = {
    ...(isChargeLaterEligible && {chargeExpiryAt: chargeDate?.toISOString()})
  }

  const hasFreeCancellation = getHasFreeCancellation(
    offer?.cancellationPenalties
  )
  const displayPrice = getDisplayPrice(offer.prices)
  const payAtProperty = hasPayAtPropertyPayment(offer)
  const totalTaxes = displayPrice?.chargeable?.taxes
  const hotelFeesBreakdown = displayPrice?.hotelFees?.breakdown || []

  const taxesAndFees = [
    ...(totalTaxes
      ? [
          {
            type: 'tax',
            total: totalTaxes,
            isPayAtProperty: false
          }
        ]
      : []),
    ...hotelFeesBreakdown.map(item => ({
      type: item.type,
      total: Number(item.total),
      isPayAtProperty: true
    }))
  ]

  const offerCheckoutUrl = getLinkByType(offer.links, ActionLinkTypes.CHECKOUT)

  const {offer_token: offerToken = null} = offerCheckoutUrl
    ? Object.fromEntries(new URLSearchParams(new URL(offerCheckoutUrl).search))
    : {}

  // 1-indexed for analytics
  const offerAnalyticsPosition = offerIndex + 1

  const offerTitleType = isOfferSoldOut
    ? ANALYTICS_OFFER_TITLE_SOLD_OUT
    : isCheapestOffer
      ? ANALYTICS_OFFER_TITLE_CHEAPEST
      : (offer.offerTitleKey ?? DEFAULT_OFFER_TITLE_KEY)

  return {
    offerId: offer.id,
    totalPrice: getTotalPrice(displayPrice),
    offerTags: offer.tags,
    availableRooms: offer.availableRooms,
    canPayLater: payAtProperty || isChargeLaterEligible,
    hasFreeCancellation,
    providerCode: offer.providerCode,
    proxyProviderCode: BOFH_PROXY_PROVIDER_CODE,
    cancellationPenalties: offer.cancellationPenalties,
    chargeType,
    cug: offer.cug,
    providerFeedId: offer.metadata?.feedID,
    providerRateType: offer.metadata?.providerRateType,
    isSelectedOffer: offer.isClicked,
    matchType: offer.matchType,
    matchedDim: offer.matchedDim,
    matchedOfferPriceDiff: offer.matchedOfferPriceDiff,
    services: offer.mealPlan?.map(mp => mp.meal) ?? [],
    payAtProperty,
    roomName: room.name,
    offerName: offerTitleType,
    offerPosition: offerAnalyticsPosition, // 1-indexed for analytics
    roomMasterId: room.masterId,
    roomPosition,
    roomId: room.id,
    numberOfImages: room.images.length,
    roomAmenities: room.amenities,
    roomDescription: room.description,
    squashedIds: 'squashedIds' in room ? room.squashedIds : [],
    displayPricePerRoomPerNight: getNightlyDisplayTotalPrice(offer.prices),
    isPrivateDeal,
    isAppExclusiveRate,
    anchorPrice,
    offerToken,
    roomSearchId,
    ctaType: flowType,
    ...(isChargeLaterEligible && {paymentExpiryAt}),
    // Price breakdown fields
    totalBasePrice: Number(displayPrice.chargeable.base),
    totalTaxesAndFees: getTotalTaxesAndFeesPrice(displayPrice),
    taxesAndFees,
    totalUpFront: getTotalUpFrontPrice(displayPrice, payAtProperty),
    totalDueAtProperty: getTotalAtProperty(displayPrice, payAtProperty)
  }
}
/**
 * Returns the hotel room context
 * @param room - room object
 * @param roomPosition - room index in array of rooms (index + 1)
 * @param offers - array of offers context
 */
export const buildRoomContext = (
  room: Room | RoomWithoutOffers,
  roomPosition: number,
  offers = [],
  roomSearchId?: string
): RoomContext => {
  if (!room) return null
  return {
    numberOfImages: room.images?.length,
    roomAmenities: room.amenities,
    keyAmenities: getTopRoomAmenities(room.amenitiesList).map(
      amenity => amenity.title
    ),
    size: room.area ?? null,
    occupancy: room.occupancy ?? null,
    bedConfig: room.bedrooms?.flatMap(b => b.bedConfigurations) ?? null,
    roomPosition,
    providerRoomName: room.raaName,
    displayedRoomName: room.name,
    roomId: room.id,
    roomMasterId: room.masterId,
    squashedIds: 'squashedIds' in room ? room.squashedIds : [],
    isSelectedRoom:
      'offers' in room ? room.offers?.some(offer => offer?.isClicked) : false,
    offers,
    roomSearchId
  }
}

export const getOfferContextFromRoomContext = (
  roomContext: RoomContext,
  offerId: string
): RoomOfferContext =>
  roomContext.offers.find(context => context.offerId === offerId)
